<script setup lang="ts">
import { computed } from "vue";
import type { PropType } from "vue";

interface Source {
    domain: string;
    source: string;
}

interface GeoItem {
    ip: string;
    label?: string;
    geo?: {
        city?: string;
        region?: string;
        country?: string;
        org?: string;
        timezone?: string;
    };
    sources?: Source[];
}

const props = defineProps({
    items: {
        type: Array as PropType<GeoItem[]>,
        required: true,
    },
});

defineEmits(["close"]);

// All members share the same coordinates, so the location is taken from the first entry.
const location = computed(() => {
    const first = props.items[0]?.geo;

    return [first?.city, first?.region, first?.country]
        .filter(Boolean)
        .join(", ");
});

function ipinfoUrl(ip: string): string {
    return `https://ipinfo.io/${ip}`;
}

function ipDetailUrl(domain: string, source: string, ip: string): string {
    return `/reports/${encodeURIComponent(domain)}/ip/${encodeURIComponent(source)}/${encodeURIComponent(ip)}`;
}
</script>

<template>
    <article class="ip-cluster">
        <button type="button" class="ip-cluster__close" @click="$emit('close')">
            ×
        </button>

        <h3 class="ip-cluster__title">
            <span class="ip-cluster__count">{{ items.length }} IP</span>
            <span v-if="location" class="ip-cluster__location">{{
                location
            }}</span>
        </h3>

        <ul class="ip-cluster__list">
            <li v-for="entry in items" :key="entry.ip" class="ip-cluster__item">
                <p class="ip-cluster__ip">{{ entry.label }} — {{ entry.ip }}</p>

                <p v-if="entry.geo?.org" class="ip-cluster__org">
                    {{ entry.geo.org }}
                </p>

                <a
                    class="ip-cluster__link"
                    :href="ipinfoUrl(entry.ip)"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    ipinfo.io/{{ entry.ip }}
                </a>

                <ul
                    v-if="(entry.sources ?? []).length"
                    class="ip-cluster__sources"
                >
                    <li
                        v-for="src in entry.sources"
                        :key="src.domain + '|' + src.source"
                        class="ip-cluster__source-item"
                    >
                        <a
                            :href="
                                ipDetailUrl(src.domain, src.source, entry.ip)
                            "
                            target="_blank"
                            rel="noopener noreferrer"
                            class="ip-cluster__source-link"
                        >
                            <span class="ip-cluster__source-domain">{{
                                src.domain
                            }}</span>
                            <span class="ip-cluster__source-sep"> / </span>
                            <span class="ip-cluster__source-name">{{
                                src.source
                            }}</span>
                        </a>
                    </li>
                </ul>
            </li>
        </ul>
    </article>
</template>

<style scoped>
.ip-cluster {
    position: relative;
    padding: 14px 16px;
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 12px 36px rgb(0 0 0 / 18%);
    font-size: 14px;
}

.ip-cluster__close {
    position: absolute;
    top: 8px;
    right: 10px;
    border: 0;
    background: transparent;
    font-size: 20px;
    cursor: pointer;
}

.ip-cluster__title {
    margin: 0 24px 8px 0;
    font-size: 15px;
    font-weight: 700;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 6px;
}

.ip-cluster__count {
    color: #1565c0;
}

.ip-cluster__location {
    font-weight: 500;
    color: #374151;
}

.ip-cluster__list {
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: 320px;
    overflow-y: auto;
}

.ip-cluster__item {
    padding: 10px 0;
    border-top: 1px solid #e5e7eb;
}

.ip-cluster__item:first-child {
    border-top: 0;
}

.ip-cluster__ip {
    margin: 0;
    font-weight: 600;
    word-break: break-all;
}

.ip-cluster__org {
    margin: 2px 0;
    color: #374151;
}

.ip-cluster__link {
    display: inline-block;
    margin-top: 4px;
    color: #1565c0;
}

.ip-cluster__sources {
    margin: 6px 0 0;
    padding: 0;
    list-style: none;
}

.ip-cluster__source-item {
    margin: 3px 0;
}

.ip-cluster__source-link {
    display: inline-flex;
    align-items: baseline;
    gap: 2px;
    max-width: 280px;
    color: #1565c0;
    text-decoration: none;
    font-size: 13px;
    line-height: 1.4;
    word-break: break-all;
}

.ip-cluster__source-link:hover {
    text-decoration: underline;
}

.ip-cluster__source-domain {
    font-weight: 600;
}

.ip-cluster__source-sep {
    color: #9ca3af;
}

.ip-cluster__source-name {
    color: #374151;
}
</style>
